import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Dropdown, Flex, Modal, Space } from 'antd-v5';
import _, { get } from 'lodash';
import { BsGrid, BsPersonFill } from 'react-icons/bs';
import { FaUserGroup } from 'react-icons/fa6';
import {
  MdChecklist,
  MdDeleteOutline,
  MdEditNote,
  MdExpandMore,
  MdOutlineContentPaste,
  MdPeopleAlt,
  MdPersonAdd,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Search, Text } from 'scouting-ui/lib';

import { AdvancementStatus } from '@appTypes/enums';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { useDeleteCounselorClassMutation } from '@modules/meritBadgeCounselors/api';
import { goToMBCBulkEntryRoute } from '@modules/meritBadgeCounselors/mbcBulkEntry/duck/actions';
import { routeMBCClassEntry } from '@modules/meritBadgeCounselors/mbcClassEntry/duck/actions';
import PrintBlueCardModal from '@modules/youthProfile/components/AdvRequirementsPage/components/PrintBlueCardModal';
import { ConditionalRender } from '@shared/components';
import { intl } from '@shared/localization';
import { toastService } from '@toasts';
import { currentUserMIDSel } from '@user/duck/selectorsTyped';

import { ViewByTabs } from '../duck/constants';
import { useYouthGroupedByClass } from '../duck/hooks';
import {
  dashboardFilterByStatusSel,
  dashboardSortByNameSel,
  dashboardViewBySel,
  mbcYouthsFilteredSel,
  selectedYouthByMBSel,
  selectedYouthSel,
} from '../duck/selectors';
import { mbcDashboardActions } from '../duck/slice';
import type { NameSortField } from '../duck/utils';
import { AssignYouthModal } from './AssignYouthModal';
import { BulkAssignYouthModal } from './BulkAssignYouthModal';
import styles from './DashboardTabYouth.module.scss';
import GenerateClassWorksheetModal from './GenerateClassWorksheetModal';
import { MBCClassModal } from './MBCClassModal';
import MBCYouthsByClassView from './MBCYouthsByClassView';
import MBCYouthsByMBView from './MBCYouthsByMBView';
import { MBCYouthsTable } from './MBCYouthsTable';
import RemoveMBModal from './RemoveMBModal';

export default function DashboardTabYouths() {
  const [showAssignYouthModal, setShowAssignYouthModal] =
    useState<boolean>(false);
  const [showClassModal, setShowClassModal] = useState<boolean>(false);
  const [editClassId, setEditClassId] = useState<number | null>(null);
  const [showBulkPrintModal, setShowBulkPrintModal] = useState<boolean>(false);
  const [showBulkAssignYouthModal, setShowBulkAssignYouthModal] =
    useState<boolean>(false);
  const memberId = useSelector(currentUserMIDSel);
  const youths = useSelector(mbcYouthsFilteredSel);
  const selectedYouth = useSelector(selectedYouthSel);
  const selectedYouthByMB = useSelector(selectedYouthByMBSel);
  const viewBy = useSelector(dashboardViewBySel);
  const filterByStatus = useSelector(dashboardFilterByStatusSel);
  const sortByName = useSelector(dashboardSortByNameSel);
  const dispatch = useDispatch();
  const enableMBCClasses = featureFlags.getFlag('SBL_6160_ENABLE_MBC_CLASS');
  const { mbClassesDict } = useYouthGroupedByClass();

  const [deleteMBCClass] = useDeleteCounselorClassMutation();

  const onClickBulkBlueCard = useCallback(
    () => setShowBulkPrintModal(!showBulkPrintModal),
    [showBulkPrintModal],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSearch = useCallback(
    _.debounce((query: string) => {
      dispatch(mbcDashboardActions.setYouthSearch(query));
    }, 300),
    [dispatch],
  );

  const handleEditMbcClass = useCallback(
    (classId: number) => (event: React.MouseEvent) => {
      event.stopPropagation();
      setShowClassModal(true);
      setEditClassId(classId);
    },
    [],
  );

  const handleDeleteMbcClass = useCallback(
    (classId: number) => (event: React.MouseEvent) => {
      event.stopPropagation();
      Modal.confirm({
        title: intl.formatMessage({ id: 'shared.areYouSure' }),
        content: intl.formatMessage({
          id: 'mbcDashboard.mbcClassModal.deleteClassMessage',
        }),
        onOk: () => {
          try {
            deleteMBCClass({
              counselorClassId: classId,
              payload: { counselorMemberId: memberId },
            });
            toastService.success(
              <FormattedMessage
                id={'mbcDashboard.mbcClassModal.deleteClass'}
              />,
            );
          } catch (error) {
            const errorMessage = get(error, ['data', 'errorDesc']);
            if (errorMessage) {
              toastService.error(errorMessage);
            }
          }
        },
      });
    },
    [deleteMBCClass, memberId],
  );

  const handleCloseClassModal = () => {
    setShowClassModal(false);
    setEditClassId(null);
  };

  // clean when unmounted
  useEffect(
    () => () => {
      dispatch(
        mbcDashboardActions.resetItem([
          'selectedYouthByMB',
          'selectedYouth',
          'youthSearch',
          'viewBy',
          'sortByName',
        ]),
      );
    },
    [dispatch],
  );

  const filterByStatusItems = [...Object.keys(AdvancementStatus), 'None'].map(
    status => ({
      label: _.startCase(status),
      key: status,
    }),
  );

  const filterByStatusItem = filterByStatusItems.find(
    item => item?.key === filterByStatus,
  ) as (typeof filterByStatusItems)[number];

  const viewByItems = [
    {
      label: 'Youth',
      key: ViewByTabs.Youth,
      icon: <BsPersonFill className={styles.segmentIcon} />,
    },
    {
      label: 'Merit Badge',
      key: ViewByTabs.MeritBadge,
      icon: <BsGrid className={styles.segmentIcon} />,
    },
    ...(enableMBCClasses
      ? [
          {
            label: 'Class',
            key: ViewByTabs.Class,
            icon: <FaUserGroup className={styles.segmentIcon} />,
          },
        ]
      : []),
  ];
  const viewByItem = viewByItems.find(
    item => item?.key === viewBy,
  ) as (typeof viewByItems)[number];

  const sortByNameItems: Array<{ label: string; key: NameSortField }> = [
    {
      label: intl.formatMessage({ id: 'mbcDashboard.actions.sortBy.lastName' }),
      key: 'lastName',
    },
    {
      label: intl.formatMessage({
        id: 'mbcDashboard.actions.sortBy.firstName',
      }),
      key: 'firstName',
    },
  ];
  const sortByNameItem = sortByNameItems.find(item => item.key === sortByName);

  const noYouthByMBSelected = selectedYouthByMB.length === 0;

  const selectedDisplay = useMemo(() => {
    if (viewBy === ViewByTabs.Youth) {
      return (
        <>
          <RemoveMBModal />
          <MBCYouthsTable youths={youths} />
        </>
      );
    }

    if (viewBy === ViewByTabs.Class) {
      return (
        <MBCYouthsByClassView
          onEdit={handleEditMbcClass}
          onDelete={handleDeleteMbcClass}
        />
      );
    }

    if (viewBy === ViewByTabs.MeritBadge) {
      return <MBCYouthsByMBView onRemove={() => null} />;
    }

    return null;
  }, [handleEditMbcClass, handleDeleteMbcClass, viewBy, youths]);

  return (
    <Flex vertical className={styles.tabContainer}>
      <Flex align="center" wrap className={styles.tableHeader}>
        <Flex align="center" wrap className={styles.toolbarActions}>
          <Button
            type="text"
            className={styles.headerBttn}
            onClick={() => setShowAssignYouthModal(true)}
          >
            <Text
              font="buttons-small"
              color="color-primary-scoutingWarmGray"
              strong
            >
              <Space align="center" size={4}>
                <FormattedMessage id="mbcDashboard.actions.addYouth" />
              </Space>
            </Text>
          </Button>
          {enableMBCClasses && (
            <Button
              type="text"
              className={styles.headerBttn}
              onClick={() => setShowClassModal(true)}
            >
              <Text
                font="buttons-small"
                color="color-primary-scoutingWarmGray"
                strong
              >
                <Space align="center" size={4}>
                  <FormattedMessage id="mbcDashboard.actions.addClass" />
                </Space>
              </Text>
            </Button>
          )}
          <Dropdown
            menu={{
              items: [
                viewBy === ViewByTabs.MeritBadge || viewBy === ViewByTabs.Youth
                  ? {
                      label: intl.formatMessage({
                        id: 'mbcDashboard.actions.removeYouth',
                      }),
                      key: 'remove-youth',
                      disabled:
                        viewBy === ViewByTabs.Youth
                          ? selectedYouth.length === 0
                          : noYouthByMBSelected,
                      icon: <MdDeleteOutline />,
                    }
                  : null,
                {
                  label: intl.formatMessage({
                    id: 'mbcDashboard.actions.bulkAddYouths',
                  }),
                  key: 'add-youths',
                  icon: <MdPersonAdd />,
                },
                {
                  label: intl.formatMessage({
                    id: 'mbcDashboard.actions.bulkEntry',
                  }),
                  key: 'bulk-entry',
                  icon: <MdEditNote />,
                },
                featureFlags.getFlag('SBL_6161_MBC_CLASS_ENTRY') &&
                enableMBCClasses
                  ? {
                      label: intl.formatMessage({
                        id: 'mbcDashboard.actions.classEntry',
                      }),
                      key: 'class-entry',
                      icon: <MdChecklist />,
                    }
                  : null,
                {
                  label: intl.formatMessage({
                    id: 'mbcDashboard.actions.classWorksheet',
                  }),
                  key: 'class-sheet',
                  disabled:
                    !featureFlags.getFlag(
                      'SBL_4163_GENERATE_CLASS_WORKSHEET',
                    ) ||
                    (viewBy === ViewByTabs.Youth
                      ? selectedYouth.length === 0
                      : selectedYouthByMB.length === 0),
                  icon: <MdPeopleAlt />,
                },
              ].filter(Boolean),
              onClick: value => {
                const key = value.key;
                switch (key) {
                  case 'remove-youth':
                    dispatch(
                      mbcDashboardActions.setModal({
                        modal: 'removeYouth',
                        open: true,
                      }),
                    );
                    break;
                  case 'add-youths':
                    setShowBulkAssignYouthModal(true);
                    break;
                  case 'class-entry':
                    dispatch(routeMBCClassEntry());
                    break;
                  case 'bulk-entry':
                    dispatch(
                      goToMBCBulkEntryRoute({
                        counselorMemberId: memberId,
                      }),
                    );
                    break;
                  case 'class-sheet':
                    dispatch(
                      mbcDashboardActions.setModal({
                        modal: 'classWorksheet',
                        open: true,
                      }),
                    );
                    break;
                  default:
                    break;
                }
              },
            }}
          >
            <Text
              font="buttons-small"
              color="color-primary-scoutingWarmGray"
              strong
              onClick={() => null}
            >
              <Space align="center" size={4}>
                <FormattedMessage id="mbcDashboard.actions.bulk" />
                <MdExpandMore />
              </Space>
            </Text>
          </Dropdown>
          <Dropdown
            menu={{
              items: viewByItems,
              onClick: value =>
                dispatch(
                  mbcDashboardActions.setViewBy(value.key as ViewByTabs),
                ),
            }}
          >
            <Text
              font="buttons-small"
              color="color-primary-scoutingWarmGray"
              strong
              onClick={() => null}
            >
              <Space align="center" size={4}>
                {viewByItem?.icon}
                <span>
                  <FormattedMessage id="mbcDashboard.actions.viewBy" />:{' '}
                </span>

                {viewByItem?.label}

                <MdExpandMore />
              </Space>
            </Text>
          </Dropdown>

          <Dropdown
            menu={{
              items: sortByNameItems,
              selectable: true,
              selectedKeys: [sortByName],
              onClick: value =>
                dispatch(
                  mbcDashboardActions.setSortByName(value.key as NameSortField),
                ),
            }}
          >
            <Text
              font="buttons-small"
              color="color-primary-scoutingWarmGray"
              strong
              onClick={() => null}
            >
              <Space align="center" size={4}>
                <span>
                  <FormattedMessage id="mbcDashboard.actions.sortBy" />:{' '}
                </span>

                {sortByNameItem?.label}

                <MdExpandMore />
              </Space>
            </Text>
          </Dropdown>

          <ConditionalRender value={viewBy === ViewByTabs.MeritBadge}>
            <Dropdown
              menu={{
                items: filterByStatusItems,
                onClick: value =>
                  dispatch(
                    mbcDashboardActions.setFilterByStatus(
                      value.key !== 'None'
                        ? (value.key as AdvancementStatus)
                        : null,
                    ),
                  ),
              }}
            >
              <Text
                font="buttons-small"
                color="color-primary-scoutingWarmGray"
                strong
                onClick={() => null}
              >
                <Space align="center" size={4}>
                  <span>
                    <FormattedMessage id="mbcDashboard.actions.filterByStatus" />
                    :{' '}
                  </span>

                  {filterByStatusItem?.label}

                  <MdExpandMore />
                </Space>
              </Text>
            </Dropdown>
          </ConditionalRender>
        </Flex>
        <Flex align="center" wrap className={styles.toolbarEnd}>
          <ConditionalRender
            value={viewBy === ViewByTabs.Youth && selectedYouth.length > 0}
          >
            <Text>({selectedYouth.length} selected)</Text>
          </ConditionalRender>
          <ConditionalRender
            value={
              viewBy === ViewByTabs.MeritBadge && selectedYouthByMB.length > 0
            }
          >
            <Text>({selectedYouthByMB.length} selected)</Text>
          </ConditionalRender>
          {featureFlags.getFlag('SBL_6727_MBC_BULK_BLUE_CARD') && (
            <Button
              type="text"
              className={styles.headerBttn}
              onClick={onClickBulkBlueCard}
            >
              <Text font="buttons-small" color="color-blue" strong>
                <Space align="center" size={4}>
                  <FormattedMessage id="mbcDashboard.bluecardBulkPrint" />
                  <MdOutlineContentPaste size="small" />
                </Space>
              </Text>
            </Button>
          )}
          <div className={styles.searchField}>
            <Search
              onChange={event => {
                const value = event.target.value;
                onSearch(value);
              }}
              placeholder={intl.formatMessage({
                id: 'mbcDashboard.actions.search',
              })}
            />
          </div>
        </Flex>
      </Flex>
      {selectedDisplay}
      {showAssignYouthModal && (
        <AssignYouthModal
          mbcMemberId={memberId}
          showModal={showAssignYouthModal}
          onClose={() => setShowAssignYouthModal(false)}
        />
      )}
      {showBulkAssignYouthModal && (
        <BulkAssignYouthModal
          mbcMemberId={memberId}
          showModal={showBulkAssignYouthModal}
          onClose={() => setShowBulkAssignYouthModal(false)}
        />
      )}
      {showClassModal && (
        <MBCClassModal
          mbcMemberId={memberId}
          showModal={showClassModal}
          classId={editClassId}
          originalMembers={
            editClassId ? mbClassesDict[editClassId]?.scouts : []
          }
          onClose={handleCloseClassModal}
        />
      )}
      {showBulkPrintModal && (
        <PrintBlueCardModal
          open={showBulkPrintModal}
          mode="mbc"
          onCancel={onClickBulkBlueCard}
        />
      )}
      <GenerateClassWorksheetModal mbcMemberId={memberId} />
    </Flex>
  );
}
